import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Check, Copy } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { Casilla } from '@/components/Casilla';
import { Seccion } from '@/components/Card';
import { copiarTiempoDeComida, leerComidasRecientes, leerDiaDeComida } from '@/db/comidas';
import { habitualesDe } from '@/db/habituales';
import { nuevoId } from '@/db/ids';
import { leerSettings } from '@/db/schema';
import { mensajeDeError } from '@/db/tx';
import type { MealEntry, TiempoComida } from '@/db/types';
import { diferenciaDias, type FechaLocal } from '@/domain/dates';
import { pendientesDelDia } from '@/domain/habituales';
import { fechaDelDia } from '@/domain/hoy';
import { NOMBRE_TIEMPO, formatearKcal, sumar, textoCantidad } from '@/domain/nutricion';
import { formatearHora, horaActual } from '@/domain/time';
import { useFechaHoy } from '@/lib/useFechaHoy';
import { CampoHora, esTiempoComida, fechaDeParametro, rutaDelDia } from './comunes';

/** Los días con ese tiempo de comida que se ofrecen para copiar. */
const DIAS_PARA_ELEGIR = 14;

const porMomento = (a: MealEntry, b: MealEntry) => a.momento.localeCompare(b.momento) || a.creadoEn.localeCompare(b.creadoEn);

/** "Tortilla de maíz, Huevo y 2 más · 320 kcal" */
function resumen(entradas: readonly MealEntry[]): string {
  const nombres = [...new Set(entradas.map((e) => e.nombre))];
  const visibles = nombres.slice(0, 2).join(', ');
  const mas = nombres.length > 2 ? ` y ${nombres.length - 2} más` : '';
  return `${visibles}${mas} · ${formatearKcal(sumar(entradas).kcal)}`;
}

function nombreDelDia(fecha: FechaLocal, hoy: FechaLocal): string {
  const dias = diferenciaDias(fecha, hoy);
  const prefijo = dias === 0 ? 'Hoy · ' : dias === 1 ? 'Ayer · ' : '';
  return `${prefijo}${fechaDelDia(fecha)}`;
}

/**
 * Copiar un tiempo de comida de otro día (Req. 18.6): se elige el día, lo que
 * se copia y la hora. Las copias llevan las mismas cantidades y valores que
 * se guardaron ese día y se agregan a lo que ya haya. Nada se copia solo.
 */
export function CopiarTiempoPage() {
  const hoy = useFechaHoy();
  const [parametros] = useSearchParams();
  const fecha = fechaDeParametro(parametros.get('fecha'), hoy);
  const tiempoParametro = parametros.get('tiempo');
  const tiempo: TiempoComida = esTiempoComida(tiempoParametro) ? tiempoParametro : 'desayuno';
  const recientes = useLiveQuery(() => leerComidasRecientes(hoy), [hoy]);
  const destino = useLiveQuery(() => leerDiaDeComida(fecha), [fecha]);
  const ajustes = useLiveQuery(() => leerSettings(), []);
  if (recientes === undefined || destino === undefined || ajustes === undefined) return null;

  // Los días con registros en ese tiempo, del más reciente al más antiguo
  const porDia = new Map<FechaLocal, MealEntry[]>();
  for (const e of recientes) {
    if (e.tiempo !== tiempo || e.fecha === fecha) continue;
    porDia.set(e.fecha, [...(porDia.get(e.fecha) ?? []), e]);
  }
  const dias = [...porDia.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .slice(0, DIAS_PARA_ELEGIR)
    .map(([dia, entradas]) => ({ dia, entradas: [...entradas].sort(porMomento) }));
  const yaHay = destino.entradas.filter((e) => e.tiempo === tiempo).length;
  const pendientes = pendientesDelDia(habitualesDe(ajustes), fecha, hoy, destino.entradas).filter((h) => h.tiempo === tiempo);

  return (
    <FormularioCopia
      key={`${fecha}-${tiempo}`}
      fecha={fecha}
      tiempo={tiempo}
      hoy={hoy}
      dias={dias}
      yaHay={yaHay}
      habitualPendiente={pendientes[0]?.nombre ?? null}
    />
  );
}

function FormularioCopia({
  fecha,
  tiempo,
  hoy,
  dias,
  yaHay,
  habitualPendiente,
}: {
  fecha: FechaLocal;
  tiempo: TiempoComida;
  hoy: FechaLocal;
  dias: Array<{ dia: FechaLocal; entradas: MealEntry[] }>;
  yaHay: number;
  habitualPendiente: string | null;
}) {
  const navegar = useNavigate();
  const nombreTiempo = NOMBRE_TIEMPO[tiempo].toLowerCase();
  const [elegido, setElegido] = useState<FechaLocal | null>(null);
  const [excluidos, setExcluidos] = useState<ReadonlySet<string>>(new Set());
  // Hoy, la hora actual (Req. 18.7); otro día, la de esa comida en el día de origen
  const [hora, setHora] = useState(() => (fecha === hoy ? horaActual() : ''));
  const [horaEditada, setHoraEditada] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // El id de cada copia se crea una vez: tocar dos veces "Copiar" no duplica
  const ids = useRef(new Map<string, string>());
  const idDe = (origenId: string) => {
    const existente = ids.current.get(origenId);
    if (existente !== undefined) return existente;
    const nuevo = nuevoId('com');
    ids.current.set(origenId, nuevo);
    return nuevo;
  };

  const origen = dias.find((d) => d.dia === elegido) ?? null;
  const incluidas = origen?.entradas.filter((e) => !excluidos.has(e.id)) ?? [];

  function elegir(dia: FechaLocal) {
    const entradas = dias.find((d) => d.dia === dia)?.entradas ?? [];
    setElegido(dia);
    setExcluidos(new Set());
    // Para otro día, la hora de esa comida en el de origen, salvo que ya se haya escrito una
    if (fecha !== hoy && !horaEditada) setHora(entradas[0]?.hora ?? '');
    setError(null);
  }

  async function copiar() {
    if (guardando || origen === null) return;
    setGuardando(true);
    setError(null);
    try {
      await copiarTiempoDeComida({
        desde: origen.dia,
        hacia: fecha,
        tiempo,
        copias: incluidas.map((e) => ({ origenId: e.id, id: idDe(e.id) })),
        hora,
      });
      void navegar(rutaDelDia(fecha, hoy, tiempo), { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        antetitulo={fechaDelDia(fecha)}
        titulo={`Copiar a ${nombreTiempo}.`}
        descripcion={`Lo que registraste en ${nombreTiempo} otro día, con sus mismas cantidades y valores. Se agrega a lo que ya tengas.`}
        volver={{ a: `/comida/agregar?fecha=${fecha}&tiempo=${tiempo}`, texto: 'Agregar', etiqueta: `Volver a agregar a ${nombreTiempo}` }}
      />

      {dias.length === 0 ? (
        <p className="text-[13px] leading-relaxed text-texto-suave">
          No hay registros de {nombreTiempo} en otros días de los últimos 60. Cuando registres uno, lo podrás copiar desde aquí.
        </p>
      ) : (
        <Seccion titulo="¿De qué día?" descripcion="Los días más recientes con algo registrado en este tiempo.">
          <div role="group" aria-label="Día para copiar" className="grid gap-2">
            {dias.map(({ dia, entradas }) => {
              const activo = dia === elegido;
              return (
                <button
                  key={dia}
                  type="button"
                  aria-pressed={activo}
                  onClick={() => elegir(dia)}
                  className={[
                    'grid min-h-14 gap-0.5 rounded-2xl border px-3 py-2.5 text-left',
                    activo ? 'border-marca bg-marca-suave' : 'border-borde bg-superficie',
                  ].join(' ')}
                >
                  <span className="flex items-center gap-1.5 text-[14px] font-medium text-texto">
                    {activo && <Check className="size-4 text-marca" aria-hidden="true" />}
                    {nombreDelDia(dia, hoy)}
                  </span>
                  {/* Sobre el acento suave, el texto secundario no llega a 4.5:1: va en el principal */}
                  <span className={`text-[12px] ${activo ? 'text-texto' : 'text-texto-suave'}`}>{resumen(entradas)}</span>
                </button>
              );
            })}
          </div>
        </Seccion>
      )}

      {origen !== null && (
        <>
          <Seccion titulo="Qué copiar" descripcion="Quita lo que esta vez no comiste.">
            <div className="grid gap-2">
              {origen.entradas.map((e) => (
                <Casilla
                  key={e.id}
                  marcada={!excluidos.has(e.id)}
                  onCambio={(marcada) =>
                    setExcluidos((actual) => {
                      const siguiente = new Set(actual);
                      if (marcada) siguiente.delete(e.id);
                      else siguiente.add(e.id);
                      return siguiente;
                    })
                  }
                >
                  {e.nombre} · {textoCantidad(e) ?? 'Registro rápido'} · {formatearKcal(e.valores.kcal)}
                </Casilla>
              ))}
            </div>
          </Seccion>

          <CampoHora
            etiqueta="Hora"
            valor={hora}
            onCambio={(v) => {
              setHora(v);
              setHoraEditada(true);
            }}
            ayuda={`Lo primero queda a esta hora y lo demás con la misma diferencia que ese día (ese día empezó a las ${formatearHora(origen.entradas[0]?.hora ?? '00:00')}). La hora sirve para calcular tu ayuno.`}
          />

          {yaHay > 0 && (
            <p className="text-[12px] leading-relaxed text-texto-suave">
              Ya tienes {yaHay === 1 ? '1 registro' : `${yaHay} registros`} en {nombreTiempo} ese día: las copias se agregan.
            </p>
          )}
          {habitualPendiente !== null && (
            <Banner tono="aviso" titulo={`Tu ${habitualPendiente.toLocaleLowerCase('es-MX')} de ese día sigue sin confirmar`}>
              Si copias esta comida, no confirmes también la habitual: se contaría dos veces.
            </Banner>
          )}
          {error !== null && (
            <Banner tono="alerta" titulo="No se copió">
              {error}
            </Banner>
          )}
          <Button icono={Copy} disabled={guardando || incluidas.length === 0} onClick={() => void copiar()}>
            {guardando
              ? 'Copiando…'
              : incluidas.length === 1
                ? 'Copiar 1 registro'
                : `Copiar ${incluidas.length} registros`}
          </Button>
        </>
      )}
    </div>
  );
}
